import React from 'react';

import { css } from '@emotion/css';
import moment from 'moment';
import {
  Bar,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
  Line,
  ComposedChart,
  Cell,
  TooltipPayload,
} from 'recharts';
import { theme } from 'theme';

import { FlexSpace } from 'components/FlexSpace';
import { Typography } from 'components/Typography';

import { ChartTooltip } from './shared/ChartTooltip';
import { calculateMovingAverage, getQuarterDomain, LARGE_PERCENTAGE_TICKS, SMALL_PERCENTAGE_TICKS } from './utils';

export interface InventoryBarChartDataPoint {
  date: string;
  [key: string]: number | string;
}

export interface InventoryBarChartBar {
  key: string;
  color: string;
  secondaryColor?: string;
  title: string;
  showInTooltip?: boolean;
  valueFormatter?: (value: number) => string;
}

export interface InventoryBarChartProps {
  data?: InventoryBarChartDataPoint[];
  bars?: InventoryBarChartBar[];
  valueFormatter: (value: number) => string;
  xAxisFormatter?: (value: string) => string;
  tooltipTitle?: (payload: TooltipPayload['payload']) => React.ReactNode;
  percentageDomain?: boolean;
  movingAverage?: boolean;
  netSum?: boolean;
  inProgressDate?: string;
  height?: number;
}

export const InventoryBarChart = ({
  data = [],
  bars = [],
  valueFormatter,
  xAxisFormatter,
  tooltipTitle,
  percentageDomain,
  movingAverage,
  netSum,
  inProgressDate,
  height = 500,
}: InventoryBarChartProps) => {
  const windowSize = 4;
  const keys = bars.map((bar) => bar.key);

  const dataWithTotals = data.map((d) => {
    const total = keys.reduce((sum: number, key: string) => sum + (Number(d[key]) || 0), 0);
    return {
      ...d,
      total,
    };
  });

  const dataWithMovingAverage = calculateMovingAverage(
    dataWithTotals.map((d) => ({ date: d.date, value: d.total })),
    windowSize,
  ).map((d, i) => ({
    ...dataWithTotals[i],
    movingAverage: d.movingAverage,
  }));

  const yAxisDomain = getQuarterDomain(dataWithMovingAverage.flatMap((d) => [d.total]));

  return (
    <ResponsiveContainer height={height} width="100%">
      <ComposedChart data={dataWithMovingAverage} height={height} stackOffset="sign">
        <XAxis dataKey="date" tickFormatter={xAxisFormatter} />
        <YAxis
          tickFormatter={(value) => valueFormatter(value)}
          domain={percentageDomain ? yAxisDomain : undefined}
          ticks={
            percentageDomain
              ? // If the minimum value is 50 or greater, use 5% ticks, otherwise use 10% ticks
                [
                  ...(yAxisDomain[0] >= 50 ? SMALL_PERCENTAGE_TICKS : LARGE_PERCENTAGE_TICKS).filter(
                    (tick) => yAxisDomain[0] <= tick && tick <= yAxisDomain[1],
                  ),
                  ...(yAxisDomain[1] === 100 ? [100] : []),
                ]
              : undefined
          }
          allowDataOverflow={percentageDomain}
        />
        <Tooltip
          content={(e: TooltipProps) => {
            if (e.active && e.payload && e.payload.length) {
              const { payload } = e.payload[0];
              return (
                <ChartTooltip>
                  {tooltipTitle && (
                    <Typography style={{ fontWeight: 500 }}>{tooltipTitle(e.payload[0].payload)}</Typography>
                  )}
                  {bars
                    .filter((bar) => bar.showInTooltip !== false && !!payload[bar.key])
                    .map((bar) => (
                      <FlexSpace key={bar.key}>
                        <div
                          className={css`
                            width: 16px;
                            height: 16px;
                            background-color: ${bar.color};
                            border-radius: 8px;
                          `}
                        />
                        <Typography>
                          {bar.title || bar.key}:{' '}
                          {bar.valueFormatter
                            ? bar.valueFormatter(payload[bar.key] || 0)
                            : valueFormatter(payload[bar.key] || 0)}
                        </Typography>
                      </FlexSpace>
                    ))}
                  {payload?.movingAverage !== undefined && movingAverage && (
                    <Typography>4 week avg: {valueFormatter(payload.movingAverage)}</Typography>
                  )}
                  {payload?.total !== undefined && netSum && (
                    <Typography>Net change: {valueFormatter(payload.total)}</Typography>
                  )}
                </ChartTooltip>
              );
            }

            return null;
          }}
        />
        <CartesianGrid strokeDasharray="3 3" />
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} stackId="stack" fill={bar.color}>
            {data.map((d, index) => {
              const isAfter = moment(d.date).isSameOrAfter(moment(inProgressDate));
              return (
                <Cell
                  key={`${bar.key}-${index}`}
                  fill={isAfter && bar.secondaryColor ? bar.secondaryColor : bar.color}
                  strokeWidth={0}
                  stroke="#fff"
                />
              );
            })}
          </Bar>
        ))}
        {movingAverage && (
          <Line type="monotone" dataKey="movingAverage" stroke={theme.colors.primary[500]} strokeWidth={3} />
        )}
        {netSum && <Line dataKey="total" stroke={theme.colors.neutral[500]} strokeWidth={3} />}
      </ComposedChart>
    </ResponsiveContainer>
  );
};
